<template>
    <div>
        <global-error-handler />

        <global-loading />

        <AppLayout v-if="shouldShowLayout" :user="user" />
        <router-view v-else />
        <!--        <default v-if="automatedSurveysChecked" />-->

        <online-status @ready="socketReady" @online-check="data => (onlineResponse = data)" />

        <user-tour v-if="user && !userTourExited" @exit="userTourExited = true" />

        <sentry-handler />

        <stale-checker />

        <release-checker />

        <enrollment-change-checker />

        <automated-survey-checker v-if="user && isSocketReady" @complete="automatedSurveysChecked = true" />

        <course-points-updated-checker />

        <forced-quiz-attempt-checker v-if="user && isSocketReady" :online-response="onlineResponse" />

        <course-key-integration-listener v-if="user && isSocketReady" />

        <quiz-editor-listener />

        <push-notifications v-if="user && $hasFeature('INBOX')" />

        <live-notifications v-if="user && $hasFeature('INBOX')" />

        <amplitude-handler />

        <session-timeout-notification />

        <chatbot-initializer />

        <!-- Buefy-style Toast (group='buefy-toast') - Top Center -->
        <Toast
            group="buefy-toast"
            position="top-center"
            :pt="{
                root: {
                    class: 'tw:w-auto'
                },
                message: {
                    class: 'tw:shadow-lg tw:min-w-0 tw:rounded-full'
                },
                messageContent: {
                    class: 'tw:flex tw:items-center'
                },
                buttonContainer: {
                    class: 'tw:hidden'
                },
            }"
        >
            <template #message="slotProps">
                <div class="tw:flex tw:items-center tw:gap-3 tw:px-6 tw:py-3 tw:rounded-md">
                    <!-- Icon based on severity -->
                    <div v-if="slotProps.message.severity" class="tw:flex-shrink-0">
                        <span
                            class="tw:text-lg"
                            :class="{
                                'tw:text-green-400': slotProps.message.severity === 'success',
                                'tw:text-red-400': slotProps.message.severity === 'error',
                                'tw:text-yellow-400': slotProps.message.severity === 'warn',
                                'tw:text-blue-400': slotProps.message.severity === 'info',
                                'tw:text-gray-800': slotProps.message.severity === 'secondary'
                            }"
                        />
                    </div>
                    <!-- Message text -->
                    <div v-if="slotProps.message.detail" class="tw:text-sm tw:font-medium">
                        {{ slotProps.message.detail }}
                    </div>
                </div>
            </template>
        </Toast>

        <!-- Default/Global Toast (no group) -->
        <Toast
            group="default"
            position="bottom-right"
            :pt="{
                message: {
                    class: 'tw:bg-gray-800 tw:text-white text-base'
                }
            }"
        >
            <template #message="slotProps">
                <div class="tw:flex tw:items-center tw:gap-4 tw:p-4 tw:rounded-lg tw:backdrop-blur-sm tw:min-w-[300px]">
                    <div v-if="slotProps.message.detail" class="tw:flex-1">
                        {{ slotProps.message.detail }}
                    </div>
                    <button
                        v-if="slotProps.message.actionLabel"
                        class="tw:px-3 tw:py-1.5 tw:rounded tw:whitespace-nowrap tw:uppercase tw:font-medium"
                        :class="{
                            'tw:text-green-500': slotProps.message.severity === 'success',
                            'tw:text-red-500': slotProps.message.severity === 'error',
                            'tw:text-yellow-500': slotProps.message.severity === 'warn',
                            'tw:text-blue-500': slotProps.message.severity === 'info'
                        }"
                        @click="handleToastAction(slotProps.message)"
                    >
                        {{ slotProps.message.actionLabel }}
                    </button>
                </div>
            </template>
        </Toast>

        <!-- Global Errors Toast (group='global-errors') -->
        <Toast
            group="global-errors"
            position="bottom-left"
            :pt="{
                message: {
                    class: 'tw:bg-gray-800 tw:text-white'
                },
                buttonContainer: {
                    class: 'tw:relative tw:right-2'
                }
            }"
        >
            <template #message="slotProps">
                <div class="tw:flex tw:items-center tw:gap-4 tw:p-4 tw:rounded-lg tw:backdrop-blur-sm tw:min-w-[300px]">
                    <div v-if="slotProps.message.detail" class="tw:flex-1">
                        {{ slotProps.message.detail }}
                    </div>
                    <button
                        v-if="slotProps.message.actionLabel"
                        class="tw:px-3 tw:py-1.5 tw:rounded tw:whitespace-nowrap tw:uppercase tw:font-medium"
                        :class="{
                            'tw:text-green-500': slotProps.message.severity === 'success',
                            'tw:text-red-500': slotProps.message.severity === 'error',
                            'tw:text-yellow-500': slotProps.message.severity === 'warn',
                            'tw:text-blue-500': slotProps.message.severity === 'info'
                        }"
                        @click="handleToastAction(slotProps.message)"
                    >
                        {{ slotProps.message.actionLabel }}
                    </button>
                </div>
            </template>
        </Toast>
        <global-dialogs />
        <global-modals />

        <!-- Styled Toast (group='styled-toast') - Rich toast with title, details, and optional action -->
        <Toast
            group="styled-toast"
            position="bottom-center"
            :pt="{
                root: { class: 'tw:w-auto' },
                message: { class: 'tw:rounded-lg tw:shadow-xl' },
                buttonContainer: { class: 'tw:hidden' }
            }"
        >
            <template #message="slotProps">
                <div class="tw:flex tw:items-center tw:gap-4 tw:px-6 tw:py-4 tw:min-w-[400px] tw:max-w-[720px]">
                    <span class="unicons uil uil-check-circle tw:text-2xl tw:flex-shrink-0" />
                    <div class="tw:flex-1 tw:text-left">
                        <div class="tw:font-medium tw:text-base tw:leading-relaxed">
                            {{ slotProps.message.summary }}
                        </div>
                        <div
                            v-if="slotProps.message.detail && slotProps.message.detail !== slotProps.message.summary"
                            class="tw:font-light tw:text-sm tw:leading-relaxed tw:opacity-90"
                        >
                            {{ slotProps.message.detail }}
                        </div>
                    </div>
                    <button
                        v-if="slotProps.message.actionLabel"
                        class="tw:flex-shrink-0 tw:px-4 tw:py-2 tw:rounded-lg tw:border tw:border-current tw:bg-transparent tw:font-medium tw:text-sm tw:cursor-pointer tw:whitespace-nowrap"
                        @click="handleToastAction(slotProps.message)"
                    >
                        {{ slotProps.message.actionLabel }}
                    </button>
                </div>
            </template>
        </Toast>

        <Toast
            group="action-toast"
            position="bottom-center"
            :pt="{
                root: { class: 'tw:w-auto' },
                message: { class: 'action-toast-message' },
                buttonContainer: { class: 'tw:hidden' }
            }"
        >
            <template #message="slotProps">
                <div class="tw:flex tw:items-center tw:gap-6 tw:px-5 tw:py-3 tw:text-white">
                    <span
                        class="tw:text-sm tw:font-normal"
                        :class="{ 'tw:text-red-400': slotProps.message.severity === 'error' }"
                    >
                        {{ slotProps.message.summary }}
                    </span>
                    <button
                        v-if="slotProps.message.actionLabel"
                        class="tw:flex-shrink-0 tw:bg-transparent tw:border-0 tw:cursor-pointer tw:font-medium tw:text-sm tw:whitespace-nowrap"
                        :class="{
                            'tw:text-red-400': slotProps.message.severity === 'error',
                            'tw:text-blue-400': slotProps.message.severity !== 'error'
                        }"
                        @click="handleToastAction(slotProps.message)"
                    >
                        {{ slotProps.message.actionLabel }}
                    </button>
                </div>
            </template>
        </Toast>

        <!-- Notification Toast Groups for Corner Positions -->
        <Toast group="top-right" position="top-right" />
        <Toast group="top-left" position="top-left" />
        <Toast group="bottom-right" position="bottom-right" />
        <Toast group="bottom-left" position="bottom-left" />
    </div>
</template>

<script>
import Toast from 'primevue/toast';
import { nextTick } from 'vue';
import { createNamespacedHelpers } from 'vuex';

import ChatbotInitializer from '@/components/chatbot/ChatbotInitializer.vue';
import GlobalErrorHandler from '@/components/GlobalErrorHandler.vue';
import LiveNotifications from '@/components/notifications/LiveNotifications.vue';
import QuizEditorListener from '@/components/quiz-builder/QuizEditorListener.vue';
import SessionTimeoutNotification from '@/components/SessionTimeoutNotification.vue';
import UserTour from '@/components/UserTour.vue';
import AmplitudeHandler from '@/components/utilities/AmplitudeHandler.vue';
import AutomatedSurveyChecker from '@/components/utilities/AutomatedSurveyChecker.vue';
import CourseKeyIntegrationListener from '@/components/utilities/CourseKeyIntegrationListener.vue';
import CoursePointsUpdatedChecker from '@/components/utilities/CoursePointsUpdatedChecker.vue';
import EnrollmentChangeChecker from '@/components/utilities/EnrollmentChangeChecker.vue';
import ForcedQuizAttemptChecker from '@/components/utilities/ForcedQuizAttemptChecker.vue';
import GlobalDialogs from '@/components/utilities/GlobalDialogs.vue';
import GlobalLoading from '@/components/utilities/GlobalLoading.vue';
import GlobalModals from '@/components/utilities/GlobalModals.vue';
import OnlineStatus from '@/components/utilities/OnlineStatus.vue';
import PushNotifications from '@/components/utilities/PushNotifications.vue';
import ReleaseChecker from '@/components/utilities/ReleaseChecker.vue';
import SentryHandler from '@/components/utilities/SentryHandler.vue';
import StaleChecker from '@/components/utilities/StaleChecker.vue';
import { initialState } from '@/Helpers.js';
import AppLayout from '@/layouts/AppLayout.vue';
import AttendanceScenario from '@/models/AttendanceScenario.js';
import axios from '@/plugins/axios.js';
import ResponseInterceptors from '@/router/interceptors/ResponseInterceptors.js';
import ImpersonationExpiryMonitor from '@/services/ImpersonationExpiryMonitor.js';
import InitialStateLoader from '@/utilities/InitialStateLoader.js';
import { shouldShowAppLayout } from '@/utilities/shouldShowAppLayout.js';

const { mapState } = createNamespacedHelpers('Auth');

export default {
    el: '#app',
    name: 'EdverumApp',

    components: {
        AppLayout,
        EnrollmentChangeChecker,
        GlobalErrorHandler,
        LiveNotifications,
        QuizEditorListener,
        ForcedQuizAttemptChecker,
        AutomatedSurveyChecker,
        CourseKeyIntegrationListener,
        CoursePointsUpdatedChecker,
        ReleaseChecker,
        StaleChecker,
        UserTour,
        OnlineStatus,
        SentryHandler,
        AmplitudeHandler,
        GlobalLoading,
        PushNotifications,
        SessionTimeoutNotification,
        ChatbotInitializer,
        Toast,
        GlobalDialogs,
        GlobalModals,
    },

    data: () => ({
        layout: null,
        isSocketReady: false,
        onlineResponse: {},
        userTourExited: false,
        automatedSurveysChecked: false,
    }),

    computed: {
        ...mapState(['user']),
        shouldShowLayout() {
            return shouldShowAppLayout(this.$route.name);
        },
    },

    watch: {
        user(user) {
            if (user) {
                this.$gate.forUser(user);

                this.$store.dispatch('User/applyPreferredTheme');
            }
        },
    },

    async mounted() {
        this.$loading = this.$refs.loading;

        await nextTick();
        ImpersonationExpiryMonitor.maybeShowNotification();
        window.SessionMonitor?.maybeShowNotification(this.$router);
    },

    async created() {
        // Events are not fired correctly when hot module replacing the App.vue component.
        // To mitigate this issue, we refresh the App.vue component in development mode when HMR is detected.
        if (import.meta.env.MODE === 'development') {
            import.meta.hot.accept(module => {
                module.default.methods.socketReady();

                const timer = setTimeout(() => {
                    window.location.reload();
                }, 3000);

                this.$dialog.confirm({
                    title: 'Refreshing...',
                    message: 'This page will refresh in 3 seconds to apply the latest changes.',
                    onCancel: () => {
                        clearTimeout(timer);
                    },
                });
            });
        }

        ResponseInterceptors(this.$store, this.$router).forEach(({ response, error }) => {
            axios.interceptors.response.use(response, error);
        });

        await AttendanceScenario.insert({
            data: initialState('AttendanceScenarios'),
        });

        this.setWindowWidth();

        await nextTick();

        window.addEventListener('resize', this.setWindowWidth);

        await InitialStateLoader.loadUserEnrollments();
        await InitialStateLoader.loadUserSettings();
        await InitialStateLoader.loadLtiToolDeployments();

        InitialStateLoader.loadCloudfrontSignedCookies();

        if (!this.user) {
            this.socketReady();

            // Set automatedSurveysChecked to true when there's no user
            // so the main layout can render for unauthenticated users
            this.automatedSurveysChecked = true;
        }

        await this.fetchFeatures();
    },

    beforeUnmount() {
        window.removeEventListener('resize', this.setWindowWidth);
    },

    metaInfo() {
        const { appName } = window.config;

        return {
            title: appName,
            titleTemplate: `%s · ${appName}`,
        };
    },

    methods: {
        socketReady() {
            this.isSocketReady = true;
        },

        async fetchFeatures() {
            this.$store.commit('SET_FEATURES', initialState('Features'));
        },

        handleToastAction(message) {
            // Call the custom onAction if provided
            if (message.onAction) {
                message.onAction();
            }
            // Close the toast using $toast.remove
            this.$toast.remove(message);
        },
    },
};
</script>

<style lang="scss">
@import '~assets/scss/toolbox.scss';

html {
    font-size: 0.9rem;
}

.p-toast-message.action-toast-message,
.action-toast-message .p-toast-message-content {
    background: #1e293b !important;
    color: #ffffff !important;
    border: 0 !important;
    border-radius: 0.75rem !important;
    box-shadow: 0 10px 25px rgba(15, 23, 42, 0.35) !important;
}

.content {
    .fr-view {
        .is-white {
            color: $black;
        }
    }
}

button.delete {
    background-color: rgba(36, 43, 54, 0.2);
    color: $white;
}

.tag:not(body) .delete {
    background-color: rgba(36, 43, 54, 0.2) !important;
}

//theme switcher for black
html.invert-black.dark-mode {
    .vuecal__view-btn--active {
        color: black;
    }

    .vuecal__cell-events-count {
        color: black;
    }
}

.navbar-dropdown.is-boxed {
    visibility: visible !important;
}

.b-sidebar {
    .sidebar-background {
        background: $loading-background;
    }

    .sidebar-content {
        &>.card {
            border-radius: 0;
        }
    }
}

.dark-mode {
    .card {
        border: 1px solid $border;

        &.has-no-box-shadow {
            border: 0;
        }
    }

    //card footer
    .card-footer {
        border-top: 1px solid $border;

        .card-footer-item {
            border: 0px solid transparent;
        }
    }

    //dashboard sidebar
    //.is-sidebar-menu {
    //    nav {
    //        ul {
    //            div {
    //                background-color: var(--p-content-background) !important;
    //            }
    //        }
    //    }
    //}

    .dropdown {
        &-content {
            border: 1px solid $border;
        }
    }

    //course sidebar menu
    #application_sidebar_summary_toggler {
        background-color: #242b36;
        color: $white;
        border: #00000000;
    }

    //coursedashboard.vue
    .course-key-link {
        color: $black;

        &:hover {
            color: $white;
        }
    }

    //quickQuizView.vue
    .modal-background-color {
        background-color: $black;
        color: $white;

        .setting-titles {
            color: $white;
        }
    }

    .snackbar .is-cancel .button {
        background-color: $black;
        color: $white !important;
    }

    .attendance-cell-text {
        color: $black;

        .has-text-subtle {
            color: black;
        }
    }

    .badge-container {
        width: 1rem;
    }

    .attendance-comment {
        left: 0;
        position: relative;
        top: -0.06rem;
    }

    //schedule editor
    .modal-close {
        background: $white;
        color: $black;
    }

    .time-period-chip {
        margin-top: 5px;
        cursor: pointer;
        background: $white !important;
        color: $black !important;
        padding: 0.25rem;
        border-radius: 0.5rem;
        border: 2px solid transparent;
    }

    td.break-week {
        background: $black;
    }

    //remediation
    .gradebook-background {
        background: var(--p-content-background) !important;
    }

    //grades
    td.grading-scheme__criteria__label {
        color: $white;
    }

    td.student-grade-view__course-grade__label {
        background: $black;
    }

    section.navbar-item.p-none:hover {
        background: none;
    }

    //reports page
    .media[data-v-d6dda450] {
        box-shadow: $card-shadow;
    }

    .content p {
        color: $white;
    }

    .media-left {
        color: $white;
    }

    .fr-view {
        .card-header-title {
            color: $white;
        }

        .is-white {
            color: $white;
        }
    }

    .navbar-item:hover {
        background: $grey;

        .navbar-link {
            background: $grey !important;
        }
    }

    .navbar-link:hover {
        background-color: $grey !important;
    }

    .sc-chat-window.opened {
        background-color: $grey !important;
        box-shadow: none;
    }

    .animation-content.modal-content {
        border: 0.1px solid white;
    }

    //enrollment groups for darkmode buttons on enrollments page
    .is-small-button {
        color: $white !important;
    }

    .is-small-button:hover {
        opacity: 1;
        border: 0px;
        padding: 15px;
        max-height: 1rem;
        max-width: 1rem;
        background-color: white;
        color: $black !important;
    }

    .modal-content {
        background-color: $grey-darker !important;
    }

    .is-activity-attachments {
        .tags.has-addons {
            background: $grey-darker;
        }
    }

    .file-chip {
        .tags.has-addons {
            span.tag.is-medium {
                color: $white;

                span {
                    color: $white;
                }
            }
        }
    }

    .submission-chip {
        .file-chip {
            .tags.has-addons {
                span.tag.is-medium {
                    color: $white;
                }
            }
        }
    }

    .vuecal__title-bar {
        button {
            color: $white;
        }
    }

    .vuecal {
        &__container {
            &::after {
                box-shadow: $card-shadow;
            }
        }
    }

    //quiz

    .quiz-timer {
        color: $black;
    }

    .quiz-question__answer.finalized.selected.free-form-answer {
        background: $black;
    }

    .is-mobile-quiz-menu-bar {
        background: $background;
        bottom: 0;
        box-shadow: $card-shadow;
        display: flex;
        height: 50px;
        padding: 0 $size-7;
        position: fixed;
        right: 0;
        top: 0;
        width: 100%;
        z-index: 1;

        @include from($navbar-breakpoint) {
            display: none;
        }

        button {
            background: transparent !important;
            color: $primary !important;
            font-size: $size-small;
            height: 50px;
            margin-left: auto;
            margin-right: -$size-7;
            box-shadow: none !important;
            padding: 0 $size-7;
            text-decoration: none !important;
        }
    }

    .quiz-menu {
        &.is-sidebar-menu {
            @include until($navbar-breakpoint) {
                background: var(--p-content-background);
                bottom: 0;
                left: 0;
                overflow-y: scroll;
                padding: 0;
                position: fixed;
                right: 0;
                top: 50px;
                width: 100%;
                z-index: 1;
            }

            @include from($navbar-breakpoint) {
                padding: $column-gap (
                    $column-gap * 2) ($column-gap * 1.5
                );
        }
    }
}

.quiz-question {
    .finalized {
        background-color: $grey-darker;
    }
}

//calendar
.calendar-widget {
    .calendar-week-selector {
        color: $white;
    }

    .vuecal {
        &__cell {
            &-events {
                div {
                    color: $white;

                    p {
                        color: $white;
                    }

                    span {
                        background-color: $white;

                        p {
                            color: $black;
                        }
                    }
                }
            }
        }
    }
}

.datepicker .datepicker-table .datepicker-body .datepicker-cell.is-selectable {
    color: $white;
}

p.message-header {
    background-color: $grey-darker;
    color: $white;
}

.datepicker .datepicker-table .datepicker-body .datepicker-cell.is-selectable:hover:not(.is-selected),
.datepicker .datepicker-table .datepicker-body .datepicker-cell.is-selectable:focus:not(.is-selected) {
    color: $white;
}

.breadcrumb {
    background-color: var(--p-content-background);
}

.b-sidebar .sidebar-content.is-white {
    background-color: var(--p-content-background) !important;
}

.is-sidebar-item {
    color: $white;
}

.modal.is-active {
    background-color: $modal-background-background-color;
}

.sticky-header {
    background: $black;
}

.quiz-builder {
    &__menu {
        background: var(--p-content-background);
    }

    &__editor,
    &__tabs {
        background-color: var(--p-content-background);
    }
}

.is-meta-panel {
    background: $background !important;
}

.mobile-nav {
    &__item {
        border-left: 1px solid $border !important;

        &:active,
        &:focus {
            border-color: $border;
        }

        >.icon {
            margin: 0 !important;
        }
    }

    .menu {
        background: var(--p-content-background);
    }
}

.late {
    background: repeating-linear-gradient(135deg,
            var(--p-content-background),
            var(--p-content-background) 10px,
            lighten($warning, 5%) 12px,
            lighten($warning, 5%) 12px);
}

.extracredit {
    background: repeating-linear-gradient(135deg,
            var(--p-content-background),
            var(--p-content-background) 10px,
            lighten($success, 30%) 12px,
            lighten($success, 30%) 12px);
}

.tag:not(body) .delete {
    background-color: #fff !important;
}
}
</style>
